import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../components/AuthContext/AuthContext";
import { bodyFields, phelobotomistBodyFields } from "../../stories/Forms/fields";
import { bodyAssessmentSchema } from "../../stories/Forms/validationSchema";
import { Row, Col, Spinner } from "react-bootstrap";
import DynamicForm from "../../stories/Forms/DynamicForm";
import {
  assessmentStatusUrl,
  assessmentValidateUrl,
  phlebotomyAssessmentStatusUrl,
} from "../../utils/constants";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import useApis from "../../services/useAPI";
import StepProgressBar from "../../stories/ProgressBars/ProgressBar";
import { XSquare } from "react-bootstrap-icons";
import {
  calculateProgressSteps,
  updateProgressAfterCompletion,
  toMatric
} from "../../utils/utils";

let submissionData;

/**
 * BodyForm Component
 * This component handles the body assessment form, including height, weight, smoking, and alcohol consumption.
 * It integrates with APIs for fetching and submitting data, handles user navigation, and displays progress.
 * @returns {JSX.Element} The body assessment form.
 */

function BodyForm() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { test_kit_id } = params;
  const [data, setData] = useState({});
  const [is_assessment_completed, setIs_assessment_completed] = useState(false);
  const { bid } = useParams();
  const { test_type } = useParams();
  const formId = location.pathname.split("/").at(-1);
  const { setCompletedSteps, setIsCompleted, setStep } = auth;

  const isPhlebotomy = location.pathname.includes("boots");

  const { callApi, resultGet, resultPost, serverError, isLoading } = useApis();

  const testTypeValue = isPhlebotomy
  ? resultGet?.phlebotomist_booking_info?.test_type
  : resultGet?.test_kit_details?.test_kit_type;

const sexValue = isPhlebotomy
  ? resultGet?.phlebotomist_booking_info?.sex
  : resultGet?.test_kit_details?.sex;

  // Update progress steps when we get test data
  useEffect(() => {
    if (resultGet) {
      const newProgressSteps = calculateProgressSteps(
        resultGet,
        testTypeValue,
        location.pathname,
      );
      auth.setProgressSteps(newProgressSteps);
    }
  }, [resultGet, location.pathname, testTypeValue]);

  useEffect(() => {
    if (!auth?.allStepData && resultGet) {
      auth.setAllStepData(resultGet?.assessment_values);
    }
  }, [resultGet]);
  const navigateToNextStep = useCallback(() => {
    const nextStep = Number.parseInt(formId) + 1;
    const navigationState = { state: { ...auth.allStepData } };

    if (isPhlebotomy && test_type && bid) {
      navigate(
        `/bloods/boots/assessment/${test_type}/${bid}/${nextStep}`,
        navigationState,
      );
    } else {
      navigate(`/assessment/${test_kit_id}/${nextStep}`, navigationState);
    }
  }, [
    isPhlebotomy,
    test_type,
    bid,
    formId,
    auth.allStepData,
    navigate,
    test_kit_id,
  ]);

  const handleAssessmentSubmit = (values) => {
    // Remove keys with undefined values
    Object.keys(values).forEach((key) => {
      if (values[key] === undefined) {
        delete values[key];
      }
    });
    const bodyData = {
      height: values.height,
      weight: values.weight,
      heightUnits: values?.heightUnits || "metric",
      weightUnits: values?.weightUnits || "metric",
    };

    if (
      values.sex === "female" ||
      sexValue === "female"
    ) {
      bodyData.ispregnant = values?.ispregnant || "No";
      bodyData.trimester = values.trimester;
    } else {
      bodyData.ispregnant = null;
      bodyData.trimester = null;
    }

    submissionData = {
      body: bodyData,
      smoking: {
        smoking: values.smoking,
        cigarettes_per_day: values.cigarettes_per_day || null,
        how_many_year: values.how_many_year || null,
        how_many_day_average: values.how_many_day_average || null,
      },
      alcohol: {
        alcohol: values.alcohol,
        alcohol_units_per_week: values.alcohol_units_per_week || null,
      },
      step: "body_smoking_alcohol",
    };

    const completedProgressSteps = updateProgressAfterCompletion(
      resultGet,
      testTypeValue,
      location.pathname,
    );
    auth.setProgressSteps(completedProgressSteps);

    if (submissionData || data) {
      callApi(
        {
          user_id: JSON.parse(localStorage.getItem("user_id")),
          ...(submissionData || {
            ...data?.body,
            ...data?.smoking,
            ...data?.alcohol,
            step: "body_smoking_alcohol",
          }),
        },
        `${assessmentValidateUrl}`,
        "POST",
        true,
      );
    }
    setData(submissionData);
    auth.setAllStepData((prevStep) => ({ ...prevStep, ...submissionData }));
  };

  // Handle POST result
  useEffect(() => {
    if (resultPost) {
      navigateToNextStep();
    }
  }, [resultPost, formId]);

  // Initial API call
  useEffect(() => {
    if (isPhlebotomy && test_type && bid) {
      callApi(null, phlebotomyAssessmentStatusUrl(bid), "GET");
    } else if (!isPhlebotomy && !test_type && !bid) {
      if (location?.state?.test_kit_id) {
        localStorage.setItem("test_kit_id", location.state.test_kit_id);
      }

      const kitId = test_kit_id || localStorage.getItem("test_kit_id");
      callApi(null, `${assessmentStatusUrl}${kitId}`, "GET", true);
    }
  }, [isPhlebotomy, test_type, bid, location?.state?.test_kit_id, test_kit_id]);

  // Handle GET result
  useEffect(() => {
    if (resultGet) {
      auth?.setTotalSteps(
        resultGet?.list_of_steps?.includes("phlebotomist_booking_info") ? 6 : 7,
      );
      const preparedData = resultGet.assessment_values;
      if (preparedData?.body?.heightUnits?.toLowerCase() === "imperial") {
        const convertedHeight = toMatric(
          preparedData.body.height,
          preparedData.body.heightUnits,
        );

        preparedData.body.height = convertedHeight.value;
        preparedData.body.heightUnits = convertedHeight.unit;
      }
      setData(preparedData);
    }
  }, [resultGet]);

  // Handle errors and completion status
  useEffect(() => {
    setIs_assessment_completed(resultGet?.is_assessment_completed);

    if (serverError) {
      auth?.showToast("Error", serverError, "../assets/mhc.png", <XSquare />);
    }
  }, [resultGet, serverError]);

  if (!data && isLoading) {
    return (
      <div className="text-center">
        <Spinner
          animation="border"
          role="output"
          className="my-custom-spinner"
        />
      </div>
    );
  }
  return (
    
    <div className="row justify-content-center start-assessment-page">
      <div className="col-md-12">
        <StepProgressBar
          steps={auth?.progressSteps?.totalSteps}
          currentStep={auth?.progressSteps?.currentStep}
          completedSteps={auth?.progressSteps?.completedSteps}
          isCompleted={auth?.progressSteps?.completedSteps}
          is_assessment_completed={false}
        />
        <div className="container-fluid">
          <Row>
            <Col md={12} className="p-0">
              <div className="mt-5">
                <div className="assessment-form mobile-responsive">
                  <Col xs={12}>
                    <DynamicForm
                      className="mb-4"
                      fields={isPhlebotomy ? phelobotomistBodyFields : bodyFields}
                      onSubmit={handleAssessmentSubmit}
                      autoHideError={true}
                      modify={true}
                      blockRefresh={true}
                      modifyFields={true}
                      validateOnChange={false}
                      validateOnBlur={false}
                      validationSchema={bodyAssessmentSchema}
                      initialValues={{
                        ...data?.body,
                        ...data?.smoking,
                        ...data?.alcohol,
                        ispregnant: data?.body?.ispregnant || "No",
                          heightUnits:
                            data?.body?.heightUnits ??
                            data?.body?.heightUnits ??
                            "metric",

                          weightUnits:
                            data?.body?.weightUnits ??
                            resultGet?.assessment_values?.body?.weightUnits ??
                            "metric",
                        height: data?.body?.height || "",
                        weight: data?.body?.weight || "",
                      }}
                      resultGet={resultGet}
                      sex={sexValue}
                      enableReinitialize={true}
                      isAssessmentCompletedForPhlebotomy={resultGet?.is_assessment_completed}
                    />
                  </Col>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
    
  );
}

export default BodyForm;
